import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  conditionSchema,
  distributeResponseSchema,
  holdingsResponseSchema,
  rosterResponseSchema,
  unitsResponseSchema,
  type Assignment,
  type Condition,
  type DistributeResponse,
} from '@unit/shared';
import { useApi } from '../../lib/useApi.js';
import { apiPost } from '../../lib/api.js';
import { useAction } from '../../lib/useAction.js';
import { ApiError } from '../../components/ApiError.js';
import { ActionError } from '../../components/ActionError.js';
import { PersonName } from '../../components/PersonName.js';

const input =
  'rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-900 focus:border-slate-500 focus:outline-none';
const primary =
  'rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-700 disabled:opacity-40';

interface Line {
  key: number;
  holding: Assignment;
  quantity: number;
  condition: Condition;
  notes: string;
}

const inHand = (a: Assignment) => a.quantity - a.expendedQuantity - a.distributedQuantity;

/**
 * Distribute from the platoon store (story 30): pick soldiers of the
 * platoon, pick lines out of what the sergeant has in hand, send one form
 * per soldier for their own signature. A serialized holding goes to one
 * soldier; a quantity holding is split per soldier and the total must fit
 * what is in hand — the server checks the same thing and refuses the whole
 * batch otherwise.
 */
export function DistributePage() {
  const { t, i18n } = useTranslation();
  const units = useApi('/units', unitsResponseSchema);
  const store = useApi('/distribution/holdings', holdingsResponseSchema);
  const platoonId = useMemo(() => {
    if (units.kind !== 'ready') return null;
    const viewable = new Set(units.data.viewableUnitIds);
    return units.data.units.find((u) => u.type === 'platoon' && u.active && viewable.has(u.id))?.id ?? null;
  }, [units]);
  const roster = useApi(platoonId ? `/units/${platoonId}/soldiers` : null, rosterResponseSchema);

  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [lines, setLines] = useState<Line[]>([]);
  const [adding, setAdding] = useState('');
  const [result, setResult] = useState<DistributeResponse | null>(null);
  const submit = useAction((body: unknown) => apiPost('/distribution', distributeResponseSchema, body));

  if (units.kind === 'error') return <ApiError code={units.code} requestId={units.requestId} />;
  if (store.kind === 'error') return <ApiError code={store.code} requestId={store.requestId} />;
  if (roster.kind === 'error') return <ApiError code={roster.code} requestId={roster.requestId} />;

  const holdings = store.kind === 'ready' ? store.data.holdings.filter((a) => inHand(a) > 0) : [];
  const soldiers = roster.kind === 'ready' ? roster.data.soldiers.filter((s) => s.status === 'active') : [];
  const label = (a: Assignment) => (i18n.language.startsWith('en') ? a.nameEn : a.nameHe);
  const available = holdings.filter((a) => !lines.some((l) => l.holding.id === a.id));
  const serializedLine = lines.some((l) => l.holding.inventoryUnitId);
  const overdrawn = lines.some((l) => (l.holding.inventoryUnitId ? 1 : l.quantity) * picked.size > inHand(l.holding));
  const canSend = picked.size > 0 && lines.length > 0 && !overdrawn && !(serializedLine && picked.size > 1) && !submit.busy;

  const addLine = (id: string) => {
    const holding = holdings.find((a) => a.id === id);
    if (!holding) return;
    setLines((prev) => [...prev, { key: Date.now(), holding, quantity: 1, condition: 'good', notes: '' }]);
    setAdding('');
  };
  const update = (key: number, patch: Partial<Line>) =>
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const send = async () => {
    const r = await submit.run({
      soldierUids: [...picked],
      lines: lines.map((l) => ({
        assignmentId: l.holding.id,
        quantity: l.holding.inventoryUnitId ? 1 : l.quantity,
        condition: l.condition,
        notes: l.notes.trim() || null,
      })),
    });
    if (r) {
      setResult(r);
      setLines([]);
      setPicked(new Set());
    }
  };

  return (
    <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200" data-testid="distribute-page">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-900">{t('distribute.title')}</h2>
        <Link to="/platoon-store" className="text-sm text-slate-500 underline-offset-4 hover:underline">
          {t('distribute.back')}
        </Link>
      </div>

      {result ? (
        <div className="mt-4 rounded-xl bg-emerald-50 p-4 ring-1 ring-emerald-200" data-testid="distribute-done">
          <p className="font-medium text-emerald-900">{t('distribute.done', { count: result.forms.length })}</p>
          <ul className="mt-2 divide-y divide-emerald-100 text-sm">
            {result.forms.map((f) => (
              <li key={f.id} className="py-1.5">
                <Link to={`/forms/${f.id}`} className="underline-offset-4 hover:underline">{f.soldierName}</Link>
                <span className="ms-2 text-emerald-800">{t('forms.itemCount', { count: f.lineItems.length })}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : holdings.length === 0 && store.kind === 'ready' ? (
        <p className="mt-3 text-sm text-slate-500" data-testid="distribute-empty">{t('distribute.noHoldings')}</p>
      ) : (
        <div className="mt-4 grid gap-6 lg:grid-cols-2">
          <div>
            <h3 className="text-xs font-medium uppercase tracking-wide text-slate-500">
              {t('distribute.soldiers')}
              {picked.size > 0 && <span className="ms-2 normal-case text-slate-400">{t('distribute.selectedCount', { count: picked.size })}</span>}
            </h3>
            <p className="mt-1 text-sm text-slate-500">{t('distribute.pickSoldiers')}</p>
            {units.kind === 'ready' && !platoonId && (
              // The role alone reaches no platoon: `viewableUnitIds` wants the
              // sergeant placed in one (or ticked on its `commanderUids`), both
              // set by the admin on the org grid. Say so, not an empty list.
              <p className="mt-2 rounded-xl bg-amber-50 p-3 text-sm text-amber-900" data-testid="distribute-no-platoon">
                {t('distribute.noPlatoon')}
              </p>
            )}
            {roster.kind === 'ready' && soldiers.length === 0 && (
              <p className="mt-2 text-sm text-slate-500" data-testid="distribute-no-soldiers">{t('distribute.noSoldiers')}</p>
            )}
            <ul className="mt-2 max-h-96 divide-y divide-slate-100 overflow-y-auto rounded-xl ring-1 ring-slate-200" data-testid="distribute-soldiers">
              {soldiers.map((s) => (
                <li key={s.uid}>
                  <label className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm hover:bg-slate-50">
                    <input
                      type="checkbox"
                      checked={picked.has(s.uid)}
                      onChange={(e) => {
                        const next = new Set(picked);
                        if (e.target.checked) next.add(s.uid);
                        else next.delete(s.uid);
                        setPicked(next);
                      }}
                      data-testid={`distribute-pick-${s.personalNumber}`}
                    />
                    <span className="text-slate-900"><PersonName person={s} /></span>
                    <span className="latin-inline ms-auto text-xs text-slate-400">{s.personalNumber}</span>
                  </label>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-xs font-medium uppercase tracking-wide text-slate-500">{t('distribute.lines')}</h3>
            <ul className="mt-2 flex flex-col gap-2" data-testid="distribute-lines">
              {lines.map((l) => {
                const per = l.holding.inventoryUnitId ? 1 : l.quantity;
                const over = per * Math.max(1, picked.size) > inHand(l.holding);
                return (
                  <li key={l.key} className={`rounded-xl p-3 ring-1 ${over ? 'ring-red-300 bg-red-50' : 'ring-slate-200'}`} data-testid="distribute-line">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="font-medium text-slate-900">
                        {label(l.holding)}
                        {l.holding.serialNumber && (
                          <span className="latin-inline ms-2 rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-700">{l.holding.serialNumber}</span>
                        )}
                      </span>
                      <span className="text-xs text-slate-500">{t('distribute.inHand', { count: inHand(l.holding) })}</span>
                    </div>
                    <div className="mt-2 flex flex-wrap items-end gap-3">
                      {l.holding.inventoryUnitId ? (
                        <span className="text-xs text-slate-500">{t('distribute.serializedHint')}</span>
                      ) : (
                        <label className="text-xs text-slate-600">
                          {t('distribute.perSoldier')}
                          <input
                            type="number"
                            min={1}
                            max={inHand(l.holding)}
                            value={l.quantity}
                            onChange={(e) => update(l.key, { quantity: Math.max(1, Number(e.target.value) || 1) })}
                            className={`${input} latin-inline mt-1 block w-24`}
                            data-testid="distribute-quantity"
                          />
                        </label>
                      )}
                      <label className="text-xs text-slate-600">
                        {t('gear.condition')}
                        <select
                          value={l.condition}
                          onChange={(e) => update(l.key, { condition: conditionSchema.parse(e.target.value) })}
                          className={`${input} mt-1 block`}
                        >
                          {conditionSchema.options.map((c) => (
                            <option key={c} value={c}>{t(`inventory.condition.${c}`)}</option>
                          ))}
                        </select>
                      </label>
                      <label className="grow text-xs text-slate-600">
                        {t('gear.notes')}
                        <input value={l.notes} onChange={(e) => update(l.key, { notes: e.target.value })} className={`${input} mt-1 block w-full`} />
                      </label>
                      <button
                        type="button"
                        onClick={() => setLines((prev) => prev.filter((x) => x.key !== l.key))}
                        className="text-xs text-red-700 underline-offset-4 hover:underline"
                      >
                        {t('distribute.remove')}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
            {available.length > 0 && (
              <label className="mt-3 block text-xs text-slate-600">
                {t('distribute.addLine')}
                <select value={adding} onChange={(e) => addLine(e.target.value)} className={`${input} mt-1 block`} data-testid="distribute-add">
                  <option value="">{t('distribute.item')}</option>
                  {available.map((a) => (
                    <option key={a.id} value={a.id}>
                      {label(a)}{a.serialNumber ? ` (${a.serialNumber})` : ` × ${inHand(a)}`}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {lines.length > 0 && picked.size > 0 && (
              <ul className="mt-3 text-xs text-slate-500" data-testid="distribute-totals">
                <li className="font-medium text-slate-700">{t('distribute.totals')}</li>
                {lines.map((l) => (
                  <li key={l.key}>
                    {label(l.holding)}: <span className="latin-inline">{(l.holding.inventoryUnitId ? 1 : l.quantity) * picked.size}</span>
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-4 flex items-center gap-3">
              <button type="button" onClick={() => void send()} disabled={!canSend} className={primary} data-testid="distribute-send">
                {submit.busy ? t('distribute.sending') : t('distribute.send')}
              </button>
            </div>
            <div className="mt-2"><ActionError error={submit.error} /></div>
          </div>
        </div>
      )}
    </section>
  );
}
